import en from './en.json';
import pl from './pl.json';
import hu from './hu.json';
import ro from './ro.json';
import sk from './sk.json';

const translations = { en, pl, hu, ro, sk };

const SUPPORTED_LANGUAGES = ['en', 'pl', 'hu', 'ro', 'sk'];
const DEFAULT_LANGUAGE = 'en';

/**
 * Get translation for a key. Supports nested keys (e.g. 'menu.dashboard')
 * and interpolation (e.g. {{name}}).
 * @param {string} lang - Language code (en, pl, hu, ro, sk)
 * @param {string} key - Translation key (e.g. 'menu.dashboard' or 'layout.welcomeUser')
 * @param {Object} params - Optional params for interpolation (e.g. { name: 'John' })
 * @returns {string} Translated string
 */
export function t(lang, key, params = {}) {
  const effectiveLang = lang && SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  const dict = translations[effectiveLang] || translations[DEFAULT_LANGUAGE];

  const keys = key.split('.');
  let value = dict;
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) break;
  }

  if (typeof value !== 'string') {
    return key;
  }

  // Interpolate {{param}} placeholders
  return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
    return params[paramKey] !== undefined ? String(params[paramKey]) : `{{${paramKey}}}`;
  });
}

/**
 * Hook to get translation function for a given language.
 * @param {string} lang - Language code from company (en, pl, hu, ro, sk, or null/empty for en)
 * @returns {function} t(key, params) - Translation function
 */
export function useTranslation(lang) {
  const effectiveLang = lang && SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  return (key, params) => t(effectiveLang, key, params);
}

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };
